import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExportMenu from "../../../Common/ExportMenu/ExportMenu";

const getExportCellValue = (row, col, index) => {
  if (typeof col.render === "function") {
    const rendered = col.render(row, index);
    if (rendered === null || rendered === undefined || typeof rendered === "object") {
      return row[col.field] ?? "N/A";
    }
    return rendered;
  }
  return row[col.field] ?? "N/A";
};

const flattenExportColumns = (columns = []) =>
  columns.flatMap((column) =>
    Array.isArray(column.children) && column.children.length > 0
      ? column.children
      : [column]
  );

const hasGroupedExportHeaders = (columns = []) =>
  columns.some(
    (column) => Array.isArray(column.children) && column.children.length > 0
  );

const mapRowsForExport = (data = [], columns = []) => {
  const flatColumns = flattenExportColumns(columns);
  let serial = 0;
  return data.map((row, index) => {
    const serialNo = row?.isTotalRow ? "" : ++serial;
    return [
      serialNo,
      ...flatColumns.map((col) => getExportCellValue(row, col, index)),
    ];
  });
};

const buildFlatHeaderLabels = (columns = []) => [
  "S.No",
  ...flattenExportColumns(columns).map((col) => col.label),
];

const buildGroupedHeaderRows = (columns = []) => {
  const topRow = ["S.No"];
  const bottomRow = [""];

  columns.forEach((column) => {
    const children =
      Array.isArray(column.children) && column.children.length > 0
        ? column.children
        : null;

    if (children) {
      topRow.push(column.label, ...Array(children.length - 1).fill(""));
      bottomRow.push(...children.map((child) => child.label));
    } else {
      topRow.push(column.label);
      bottomRow.push("");
    }
  });

  return [topRow, bottomRow];
};

const buildPdfGroupedHead = (columns = []) => {
  const topRow = [{ content: "S.No", rowSpan: 2 }];
  const bottomRow = [];

  columns.forEach((column) => {
    const children =
      Array.isArray(column.children) && column.children.length > 0
        ? column.children
        : null;

    if (children) {
      topRow.push({ content: column.label, colSpan: children.length });
      children.forEach((child) => {
        bottomRow.push(child.label);
      });
    } else {
      topRow.push({ content: column.label, rowSpan: 2 });
    }
  });

  return [topRow, bottomRow];
};

const applyExcelHeaderCellStyle = (cell) => {
  cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
  cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF003366" },
  };
  cell.border = {
    top: { style: "thin", color: { argb: "FFCCCCCC" } },
    left: { style: "thin", color: { argb: "FFCCCCCC" } },
    bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
    right: { style: "thin", color: { argb: "FFCCCCCC" } },
  };
};

const DelhiReportExport = ({
  reportData = [],
  reportTitle = "Delhi Report",
  columns = [],
  fileName = "DelhiReport",
}) => {
  const flatHeaderLabels = buildFlatHeaderLabels(columns);
  const useGroupedHeaders = hasGroupedExportHeaders(columns);
  const columnCount = flatHeaderLabels.length;

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(reportTitle);
    const boldFont = { bold: true };

    const currentDate = new Date();
    const formattedDate = `${currentDate.getDate()}-${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`;
    const formattedTime = `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;
    const downloadDateTime = `${reportTitle} Generated on: ${formattedDate}, ${formattedTime}`;

    const titleRow = worksheet.addRow([`${reportTitle} List`]);
    worksheet.mergeCells(1, 1, 1, columnCount);
    titleRow.getCell(1).font = { bold: true, size: 14 };
    titleRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };

    const dateRow = worksheet.addRow([downloadDateTime]);
    worksheet.mergeCells(2, 1, 2, columnCount);
    dateRow.getCell(1).font = boldFont;
    dateRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };

    if (useGroupedHeaders) {
      const [topRowLabels, bottomRowLabels] = buildGroupedHeaderRows(columns);
      const topHeaderRow = worksheet.addRow(topRowLabels);
      const bottomHeaderRow = worksheet.addRow(bottomRowLabels);

      let colIndex = 1;
      // S.No spans both header rows
      worksheet.mergeCells(3, colIndex, 4, colIndex);
      applyExcelHeaderCellStyle(topHeaderRow.getCell(colIndex));
      applyExcelHeaderCellStyle(bottomHeaderRow.getCell(colIndex));
      colIndex += 1;

      columns.forEach((column) => {
        const children =
          Array.isArray(column.children) && column.children.length > 0
            ? column.children
            : null;

        if (children) {
          const startCol = colIndex;
          const endCol = colIndex + children.length - 1;
          worksheet.mergeCells(3, startCol, 3, endCol);
          for (let i = startCol; i <= endCol; i += 1) {
            applyExcelHeaderCellStyle(topHeaderRow.getCell(i));
            applyExcelHeaderCellStyle(bottomHeaderRow.getCell(i));
          }
          colIndex = endCol + 1;
        } else {
          worksheet.mergeCells(3, colIndex, 4, colIndex);
          applyExcelHeaderCellStyle(topHeaderRow.getCell(colIndex));
          applyExcelHeaderCellStyle(bottomHeaderRow.getCell(colIndex));
          colIndex += 1;
        }
      });
    } else {
      const tableHeaderRow = worksheet.addRow(flatHeaderLabels);
      tableHeaderRow.eachCell({ includeEmpty: true }, (cell) => {
        applyExcelHeaderCellStyle(cell);
      });
    }

    mapRowsForExport(reportData, columns).forEach((row) => {
      const dataRow = worksheet.addRow(row);
      dataRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin", color: { argb: "FFDDDDDD" } },
          left: { style: "thin", color: { argb: "FFDDDDDD" } },
          bottom: { style: "thin", color: { argb: "FFDDDDDD" } },
          right: { style: "thin", color: { argb: "FFDDDDDD" } },
        };
      });
    });

    flatHeaderLabels.forEach((label, index) => {
      const column = worksheet.getColumn(index + 1);
      column.width = Math.max(14, String(label).length + 2);
    });

    try {
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${fileName}.xlsx`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating Excel file:", error);
    }
  };

  const exportToPDF = async () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const currentDate = new Date();
    const formattedDate = `${currentDate.getFullYear()}-${currentDate.getMonth() + 1}-${currentDate.getDate()}`;
    const formattedTime = `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;
    const downloadDateTime = `${reportTitle} Generated on: ${formattedDate} ${formattedTime}`;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(`${reportTitle} List`, 14, 20);
    doc.setFontSize(10);
    doc.text(downloadDateTime, 14, 28);

    const head = useGroupedHeaders
      ? buildPdfGroupedHead(columns)
      : [flatHeaderLabels];

    autoTable(doc, {
      startY: 35,
      head,
      body: mapRowsForExport(reportData, columns),
      headStyles: {
        fillColor: [0, 51, 102],
        textColor: [255, 255, 255],
        fontSize: 8,
        halign: "center",
        valign: "middle",
      },
      bodyStyles: {
        fontSize: 7,
        halign: "center",
      },
    });

    doc.save(`${fileName}.pdf`);
  };

  return (
    <ExportMenu
      items={[
        { label: "Excel", icon: "fa-solid fa-file-excel", onClick: exportToExcel },
        { label: "PDF", icon: "fa-solid fa-file-pdf", onClick: exportToPDF },
      ]}
    />
  );
};

export default DelhiReportExport;
